/**
 * The consent gates.
 *
 * A gate is a server-side precondition, not a screen. The endpoint behind it refuses to act without
 * a valid, current, correctly-scoped record. This file is imported by the API (to enforce) and by
 * both portals (to present) so the two can never disagree about what is required.
 *
 * See docs/04-consent-framework.md.
 */

export const CONSENT_GATES = [
  'purchase_educational_nature',
  'no_clinical_claims',
  'not_for_emergencies',
  'questionnaire_not_professional_care',
  'emotional_awareness_responsibility',
  'submission_interpretation_responsibility',
  'booking_educational_session',
  'photo_upload',
  'sensitive_information_optional',
  'terms_of_service',
  'privacy_policy',
  'marketing_optional',
] as const;

export type ConsentGate = (typeof CONSENT_GATES)[number];

/** What a record is tied to. A consent given for one purchase never covers another. */
export type ConsentScope = 'user' | 'purchase' | 'submission';

/**
 * How long a record stays valid.
 * - `version`  — until the document is republished.
 * - `attempt`  — additionally invalidated when a submission is returned for changes, so a
 *                resubmission always requires fresh consent rather than silently reusing the old one.
 */
export type ConsentFreshness = 'version' | 'attempt';

export interface ConsentGateDefinition {
  readonly gate: ConsentGate;
  readonly scope: ConsentScope;
  readonly freshness: ConsentFreshness;
  /** False only for the marketing opt-in. Everything else blocks the journey. */
  readonly required: boolean;
  /** The client must reach the end of the text before the checkbox enables. */
  readonly requiresScrollToEnd: boolean;
  /** Where in the journey it is asked — used by the admin consent log and by the portal. */
  readonly moment: string;
  /** The API operation it guards. Documentation, and the key the enforcement middleware reports. */
  readonly guards: string;
}

export const CONSENT_GATE_DEFINITIONS: Readonly<Record<ConsentGate, ConsentGateDefinition>> = {
  purchase_educational_nature: {
    gate: 'purchase_educational_nature',
    scope: 'purchase',
    freshness: 'version',
    required: true,
    requiresScrollToEnd: true,
    moment: 'Acknowledgement screen, before payment',
    guards: 'POST /v1/checkout/intent',
  },
  /**
   * Added from the client's build brief, section 4.3. It is deliberately separate from
   * `purchase_educational_nature`: one says what the service *is*, this says what it never does.
   * Bundling them would make it impossible to say afterwards which of the two a person agreed to,
   * which is the only question anyone ever asks.
   */
  no_clinical_claims: {
    gate: 'no_clinical_claims',
    scope: 'purchase',
    freshness: 'version',
    required: true,
    requiresScrollToEnd: false,
    moment: 'Acknowledgement screen, before payment',
    guards: 'POST /v1/checkout/intent',
  },
  /**
   * The safety acknowledgement. Its own gate because it is the one a person may need to act on in a
   * hurry, and because an emergency is the single case where the right answer is to leave this site.
   */
  not_for_emergencies: {
    gate: 'not_for_emergencies',
    scope: 'purchase',
    freshness: 'version',
    required: true,
    requiresScrollToEnd: false,
    moment: 'Acknowledgement screen, before payment',
    guards: 'POST /v1/checkout/intent',
  },
  questionnaire_not_professional_care: {
    gate: 'questionnaire_not_professional_care',
    scope: 'submission',
    freshness: 'version',
    required: true,
    requiresScrollToEnd: true,
    moment: 'Before the reflective exercise opens',
    guards: 'POST /v1/submissions/{id}/start',
  },
  emotional_awareness_responsibility: {
    gate: 'emotional_awareness_responsibility',
    scope: 'submission',
    freshness: 'version',
    required: true,
    requiresScrollToEnd: true,
    moment: 'Before the reflective exercise opens, recorded separately',
    guards: 'POST /v1/submissions/{id}/start',
  },
  submission_interpretation_responsibility: {
    gate: 'submission_interpretation_responsibility',
    scope: 'submission',
    freshness: 'attempt',
    required: true,
    requiresScrollToEnd: true,
    moment: 'Final submission, and again on every resubmission',
    guards: 'POST /v1/submissions/{id}/submit',
  },
  booking_educational_session: {
    gate: 'booking_educational_session',
    scope: 'purchase',
    freshness: 'version',
    required: true,
    requiresScrollToEnd: true,
    moment: 'Before scheduling a live session',
    guards: 'POST /v1/sessions/booking-token',
  },
  photo_upload: {
    gate: 'photo_upload',
    scope: 'submission',
    freshness: 'version',
    required: true,
    requiresScrollToEnd: false,
    moment: 'Before the first image is uploaded',
    guards: 'POST /v1/submissions/{id}/uploads/presign',
  },
  sensitive_information_optional: {
    gate: 'sensitive_information_optional',
    scope: 'submission',
    freshness: 'version',
    required: true,
    requiresScrollToEnd: false,
    moment: 'Before any optional, personal question is answered',
    guards: 'PUT /v1/submissions/{id}/responses/{questionKey} (sensitive questions only)',
  },
  terms_of_service: {
    gate: 'terms_of_service',
    scope: 'user',
    freshness: 'version',
    required: true,
    requiresScrollToEnd: false,
    moment: 'Account creation',
    guards: 'POST /v1/account/claim',
  },
  privacy_policy: {
    gate: 'privacy_policy',
    scope: 'user',
    freshness: 'version',
    required: true,
    requiresScrollToEnd: false,
    moment: 'Account creation',
    guards: 'POST /v1/account/claim',
  },
  marketing_optional: {
    gate: 'marketing_optional',
    scope: 'user',
    freshness: 'version',
    // The only gate that may be declined with no consequence. Never pre-checked, never bundled
    // with a required consent — that bundling is exactly the pattern regulators penalise.
    required: false,
    requiresScrollToEnd: false,
    moment: 'Anywhere, always optional',
    guards: 'nothing',
  },
};

export function gateDefinition(gate: ConsentGate): ConsentGateDefinition {
  return CONSENT_GATE_DEFINITIONS[gate];
}

export function isConsentGate(value: string): value is ConsentGate {
  return (CONSENT_GATES as readonly string[]).includes(value);
}

/** The gates that must be satisfied before a given API operation may proceed. */
export const GATES_BY_OPERATION = {
  checkout: ['purchase_educational_nature', 'no_clinical_claims', 'not_for_emergencies'],
  claimAccount: ['terms_of_service', 'privacy_policy'],
  startSubmission: ['questionnaire_not_professional_care', 'emotional_awareness_responsibility'],
  submitSubmission: ['submission_interpretation_responsibility'],
  presignUpload: ['photo_upload'],
  answerSensitiveQuestion: ['sensitive_information_optional'],
  bookingToken: ['booking_educational_session'],
} as const satisfies Record<string, readonly ConsentGate[]>;

export type ConsentOperation = keyof typeof GATES_BY_OPERATION;
